import DesignSystem from 'design-system-utils'

const gradientStart = '#62bdfe'
const gradientStop = '#6833cb'

/* eslint-disable no-dupe-keys */
export const palette = {
  electricBlue: '#0C64FF',
  electricBlueHover: '#004aca',
  blueNight: '#1F2245',
  magneticBlack: '#001A30',
  babyBlue: '#99CAFF',
  cloudBlue: '#bedff8',
  gradient: `linear-gradient(to right, ${gradientStart}, ${gradientStop})`,
  progressionGradient: 'linear-gradient(90deg, #007AFF 0%, #F1F2F3 100%)',
  blockGradient: 'linear-gradient(180deg, #FCFCFD 0%, #EFF2F6 100%)',
  transparentBlack: 'rgba(0, 0, 0, 0.1)',
  brightOrange: '#FF5C39',
  orange: '#FFA20C',
  darkOrange: '#4a0d00',
  skyBlue: '#62BDFE',
  greyLighter: '#F7F8FA',
  greyLight: '#F1F2F3',
  greyMedium: '#E8EAEE',
  grey: '#DBDBDE',
  greyDark: '#ABADC2',
  greyDarker: '#767992',
  greyBlue: '#EFF4FF',
  white: '#FFFFFF',
  red: '#FF0060',
  gradientStart,
  gradientStop,
  lightGreen: '#CBE8CE',
  yellow: '#FECC35',
  lightYellow: '#FFE493',
  mediumGreen: '#4EF084',
  tealGreen: '#35A88D',
  purple: '#db6ddb',
  darkBlue: '#195B8E',
  green: 'rgba(6, 182, 0, 1)',
}

export type Palette = keyof typeof palette

const baseFontSize = 12

export type Tokens = typeof tokens

export const tokens = {
  type: {
    baseFontSize: `${baseFontSize}`,

    modularscale: {
      base: '12px', // should be the same as baseFontSize
      ratio: 1.5,
    },

    sizes: {
      xs: 9,
      sm: 10,
      md: 11,
      regular: 12,
      lg: 13,
      xl: 14,
      '2xl': 16,
      '3xl': 22,
      '4xl': 26,
      '5xl': 30,

      buttons: {
        xs: 11,
        default: 13,
      },
      links: {
        xs: 12,
        default: 13,
      },
      inputs: {
        default: {
          label: 11,
          value: 14,
        },
        times: {
          indicator: 16,
        },
        check: 13,
      },
      stepper: {
        steps: 12,
      },
      paragraph: {
        default: 14,
      },
      headings: {
        level0: 28,
        level1: 24,
        level2: 20,
        level3: 14,
        level4: 12,
      },
    },

    lineHeight: {
      inputs: 23,
    },

    fontWeight: {
      normal: 'normal', // Useful to set here if using anything other than `normal`
      bold: 'bold', // Useful to set here when bold webfonts come as 400 font-weight.
      headings: 'bold', // instead of browser default, bold
    },
  },

  colors: {
    ...palette,
    brand: palette,
    backgrounds: {
      inputs: palette.white,
      listDetails: palette.white,
      checkedInputs: palette.electricBlue,
      timeInputDials: palette.greyLighter,
      blockGradient: palette.blockGradient,
      progress: {
        todo: palette.greyMedium,
        doing: palette.progressionGradient,
        done: palette.electricBlue,
      },
    },
    icons: {
      steppers: {
        todo: palette.greyDark,
        done: palette.electricBlue,
      },
    },
    borders: {
      inputs: {
        default: palette.grey,
        error: palette.brightOrange,
      },
      listDetails: palette.greyLight,
      fieldsets: {
        default: palette.greyMedium,
      },
    },
    texts: {
      default: palette.blueNight,
      stepper: {
        todo: palette.greyDarker,
        done: palette.skyBlue,
      },
      inputs: {
        label: palette.greyDarker,
        value: palette.magneticBlack,
        error: palette.brightOrange,
        times: {
          default: palette.magneticBlack,
          active: palette.electricBlue,
        },
      },
      listDetails: {
        legend: palette.greyDarker,
        details: {
          default: palette.blueNight,
          minor: palette.greyDarker,
        },
      },
    },
  },

  viewports: {
    xs: { width: 320, height: 600 },
    sm: { width: 768, height: 600 },
    md: { width: 991, height: 600 },
    lg: { width: 1280, height: 600 },
    xl: { width: 1440, height: 600 },
    xxl: { width: 1600, height: 900 },
  },

  zIndex: {
    normal: 0,
    high: 2,
    higher: 5,
    dropdowns: 50,
  },

  spacing: {
    padding: {
      inputs: 10,
    },
  },

  border: {
    radius: {
      inputs: 3,
      buttons: 3,
      fieldsets: 3,
      messages: 3,
      card: 3,
    },
    style: 'solid',
    width: {
      inputs: 1,
      fieldsets: 1,
      buttons: 1,
    },
  },

  layout: {
    gutter: 30,
    maxWidth: {
      sm: 750,
      md: 970,
      lg: 1170,
      xl: 1280,
      xxl: 1360,
    },
    grid: {
      columnCount: 12,
      columnWidth: 50,
    },
  },
  boxShadow: {
    light: `
      0 ${2 / baseFontSize}rem ${4 / baseFontSize}rem 0 rgba(0, 0, 0, 0.05),
      0 0 ${4 / baseFontSize}rem 0 rgba(0, 0, 0, 0.05)`,
    medium: `
      0 ${2 / baseFontSize}rem ${8 / baseFontSize}rem rgba(172,172,172,0.1)`,
  },
}

export default new DesignSystem(tokens as unknown, {
  fontSizeUnit: 'rem',
})
/* eslint-enable no-dupe-keys */
